/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import Joi from '@hapi/joi';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { IWithReactRouterProps } from '../../mixins/router/withReactRouter';
import { ITranslateProps, translate } from '../../mixins/translate';
import { ILanguageCodeProps, IValidationComponentState, ValidatingComponent } from '../../mixins/validation/validation';
import { fetchGet, fetchPost } from '../../store/fetch';
import { Roles } from '../../utils/enums/Roles';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import {
    Content,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelHeader,
    EditPanelInput,
    EditPanelRow,
    EditPanelSection,
    EditPanelSelect,
    EditPanelSwitch,
    Panel,
    PanelBody,
    Topbar,
    TopbarCrumb,
    TopbarCrumbActive,
    TopbarLeft,
    Tray,
} from '../elements';
import { CountrySelect } from './CountrySelect';

interface ICompanyAddEditProps extends ITranslateProps, ILanguageCodeProps, IWithReactRouterProps {
    dispatch: (prop: any) => void;
    userRole: Roles;
    match: { params: any };
}

interface ICompanyAddEditState extends IValidationComponentState {
    message?: string;
    submitting: boolean;
    failed: boolean;
    errorCode?: string;
    name?: string;
    storageMb?: string;
    status?: string;
    role?: Roles;
    state?: string;
    city?: string;
    zip?: string;
    street?: string;
    country?: string;
    isClient?: boolean;
    useBatches: boolean;
    useSurveyComments: boolean;
    useNen2767Advanced: boolean;
    authenticationToken?: string;
    latitude?: number;
    longitude?: number;
    isPdokMapLayerAvailable: boolean;
    areLabelsVisibleInOverviews: boolean;
    isDroneNoFlyMapNlAvailable: boolean;
    shouldClusterMarkers: boolean;
    restrictedHostName: string;
    useObjectRelations: boolean;
    shouldEnforceMfa: boolean;
    useEquipmentManagement: boolean;
    useProductTitle: boolean;
    daysVisibilityFutureSurveys: number;
}

function mapStateToProps(state: any) {
    const { authentication, language } = state;
    return {
        userRole: authentication.role,
        languageCode: language.code,
    };
}

export class CompanyAddEditComponent extends ValidatingComponent<ICompanyAddEditProps, ICompanyAddEditState> {
    constructor(props: ICompanyAddEditProps) {
        super(props);

        this.state = {
            status: 'enabled',
            country: 'NL',
            name: '',
            state: '',
            city: '',
            zip: '',
            street: '',
            storageMb: '',
            useBatches: false,
            useSurveyComments: false,
            useNen2767Advanced: false,
            isPdokMapLayerAvailable: true,
            areLabelsVisibleInOverviews: false,
            isDroneNoFlyMapNlAvailable: false,
            shouldClusterMarkers: false,
            submitting: false,
            failed: false,
            errors: {},
            restrictedHostName: '',
            useObjectRelations: false,
            shouldEnforceMfa: false,
            useEquipmentManagement: false,
            useProductTitle: false,
            daysVisibilityFutureSurveys: 30,
        };
    }

    public override componentDidMount() {
        if (this.props.match.params.id) {
            void this.load();
        }
    }

    public override componentDidCatch(error: any) {
        void this.setState({ submitting: false, errorCode: error, failed: true });
    }

    public getValidatorTypes(): any {
        return {
            name: Joi.string().required().label(this.props.t('company.companyName')),
            storageMb: Joi.number().allow('').integer().label(this.props.t('company.storageMb')),
            role: Joi.string().required().label(this.props.t('company.companyRole')),
            latitude: Joi.number().label(this.props.t('object.latitude')).allow(null, ''),
            longitude: Joi.number().label(this.props.t('object.longitude')).allow(null, ''),
        };
    }

    public getValidatorData() {
        return {
            name: this.state.name,
            storageMb: this.state.storageMb,
            status: this.state.status,
            role: this.state.role,
            state: this.state.state,
            city: this.state.city,
            zip: this.state.zip,
            street: this.state.street,
            country: this.state.country,
            isClient: this.state.isClient,
            useBatches: this.state.useBatches,
            useSurveyComments: this.state.useSurveyComments,
            useNen2767Advanced: this.state.useNen2767Advanced,
            authenticationToken: this.state.authenticationToken,
            latitude: this.state.latitude,
            longitude: this.state.longitude,
            isPdokMapLayerAvailable: this.state.isPdokMapLayerAvailable,
            areLabelsVisibleInOverviews: this.state.areLabelsVisibleInOverviews,
            isDroneNoFlyMapNlAvailable: this.state.isDroneNoFlyMapNlAvailable,
            shouldClusterMarkers: this.state.shouldClusterMarkers,
            restrictedHostName: this.state.restrictedHostName,
            useObjectRelations: this.state.useObjectRelations,
            shouldEnforceMfa: this.state.shouldEnforceMfa,
            useEquipmentManagement: this.state.useEquipmentManagement,
            useProductTitle: this.state.useProductTitle,
            daysVisibilityFutureSurveys: this.state.daysVisibilityFutureSurveys,
        };
    }
    public override render() {
        return (
            <>
                <Topbar>
                    <TopbarLeft>
                        <TopbarCrumbActive title={this.state.name} icon="fa-building" />
                        <TopbarCrumb>
                            <Link to={`/companies/${this.props.match.params.id}`}>{this.props.t('company.details')}</Link>
                        </TopbarCrumb>
                        <TopbarCrumb>
                            <Link to="/companies">{this.props.t('common.companies')}</Link>
                        </TopbarCrumb>
                    </TopbarLeft>
                </Topbar>
                <Content>
                    <Tray>
                        {this.renderMessage()}
                        <Panel small>
                            <form method="post" action="/" id="admin-form">
                                <PanelBody>
                                    {/* company name */}
                                    <EditPanelSection>
                                        <EditPanelInput
                                            name="name"
                                            placeholder={this.props.t('company.companyName')}
                                            value={this.state.name}
                                            icon="fa fa-briefcase"
                                            handleChange={this.handleChange.bind(this)}
                                            notValid={!this.isValid('name')}
                                            handleBlur={() => this.handleValidation()}
                                        />
                                        {this.renderHelpText(this.getValidationMessages('name'))}
                                    </EditPanelSection>
                                    <EditPanelSection>
                                        <EditPanelInput
                                            name="street"
                                            placeholder={this.props.t('company.streetAndNumber')}
                                            value={this.state.street}
                                            icon="fa fa-road"
                                            handleChange={this.handleChange.bind(this)}
                                        />
                                    </EditPanelSection>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelInput
                                                name="zip"
                                                placeholder={this.props.t('company.zipCode')}
                                                value={this.state.zip}
                                                icon="fa fa-square-o"
                                                handleChange={this.handleChange.bind(this)}
                                            />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelInput
                                                name="city"
                                                placeholder={this.props.t('company.city')}
                                                value={this.state.city}
                                                icon="fa fa-square-o"
                                                handleChange={this.handleChange.bind(this)}
                                            />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelInput
                                                name="state"
                                                placeholder={this.props.t('company.state')}
                                                value={this.state.state}
                                                icon="fa fa-square-o"
                                                handleChange={this.handleChange.bind(this)}
                                            />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <CountrySelect
                                                initialValue={this.state.country}
                                                onChange={async (country) => this.handleChange(country, 'country')}
                                            />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    {/* role */}
                                    <EditPanelHeader text={this.props.t('common.settings')} />
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelSelect
                                                name="role"
                                                value={this.state.role}
                                                topLabel={this.props.t('company.companyCompanyRole')}
                                                handleChange={this.handleChange.bind(this)}
                                                handleBlur={() => this.handleValidation()}
                                                disabled={this.state.submitting}
                                                notValid={!this.isValid('role')}
                                                placeholder={this.props.t('common.selectItem', this.props.t('company.companyRole'))}
                                                options={[
                                                    ...[],
                                                    ...(this.props.userRole === Roles.Administrator
                                                        ? [{ value: Roles.Administrator, text: this.props.t(`common.${Roles.Administrator}`) }]
                                                        : []),
                                                    ...(this.props.userRole !== Roles.Owner
                                                        ? [{ value: Roles.Owner, text: this.props.t(`common.${Roles.Owner}`) }]
                                                        : []),
                                                    ...(this.props.userRole !== Roles.Surveyor
                                                        ? [{ value: Roles.Surveyor, text: this.props.t(`common.${Roles.Surveyor}`) }]
                                                        : []),
                                                    ...(this.props.userRole !== Roles.Operator
                                                        ? [{ value: Roles.Operator, text: this.props.t(`common.${Roles.Operator}`) }]
                                                        : []),
                                                ]}
                                            />
                                            {this.renderHelpText(this.getValidationMessages('role'))}
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelSelect
                                                name="status"
                                                value={this.state.status}
                                                handleChange={this.handleChange.bind(this)}
                                                disabled={this.state.submitting}
                                                topLabel={this.props.t('common.status')}
                                                placeholder={this.props.t('common.selectItem', this.props.t('common.status'))}
                                                options={[
                                                    { value: 'enabled', text: this.props.t('common.enabled') },
                                                    { value: 'disabled', text: this.props.t('common.disabled') },
                                                ]}
                                            />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelSection>
                                        <EditPanelSwitch
                                            name="shouldEnforceMfa"
                                            placeholder={this.props.t('company.shouldEnforceMfa')}
                                            value={this.state.shouldEnforceMfa}
                                            handleChange={this.handleChange.bind(this)}
                                            toolTip={this.props.t('company.shouldEnforceMfaExplanation')}
                                        />
                                    </EditPanelSection>
                                    {this.props.userRole === Roles.Administrator && (
                                        <>
                                            <EditPanelSection>
                                                <EditPanelSwitch
                                                    name="isClient"
                                                    placeholder={this.props.t('company.isClient')}
                                                    value={this.state.isClient}
                                                    handleChange={this.handleChange.bind(this)}
                                                    toolTip={this.props.t('company.isClientExplanation')}
                                                />
                                            </EditPanelSection>

                                            {(this.state.isClient || this.state.role === Roles.Administrator) && !isCustomerVersion('ronik') && (
                                                <>
                                                    <EditPanelHeader text={this.props.t('company.clientSettings')} />
                                                    <EditPanelSection>
                                                        <EditPanelSwitch
                                                            name="useBatches"
                                                            placeholder={this.props.t('company.useBatches')}
                                                            value={this.state.useBatches}
                                                            handleChange={this.handleChange.bind(this)}
                                                            toolTip={this.props.t('company.useBatchesExplanation')}
                                                        />
                                                    </EditPanelSection>
                                                    <EditPanelSection>
                                                        <EditPanelSwitch
                                                            name="useSurveyComments"
                                                            placeholder={this.props.t('company.useSurveyComments')}
                                                            value={this.state.useSurveyComments}
                                                            handleChange={this.handleChange.bind(this)}
                                                            toolTip={this.props.t('company.useSurveyCommentsExplanation')}
                                                        />
                                                    </EditPanelSection>
                                                    <EditPanelSection>
                                                        <EditPanelSwitch
                                                            name="useProductTitle"
                                                            placeholder={this.props.t('company.useProductTitle')}
                                                            value={this.state.useProductTitle}
                                                            handleChange={this.handleChange.bind(this)}
                                                            toolTip={this.props.t('company.useProductTitleExplanation')}
                                                        />
                                                    </EditPanelSection>

                                                    <EditPanelSection>
                                                        <EditPanelSwitch
                                                            name="areLabelsVisibleInOverviews"
                                                            placeholder={this.props.t('company.areLabelsVisibleInOverviews')}
                                                            value={this.state.areLabelsVisibleInOverviews}
                                                            handleChange={this.handleChange.bind(this)}
                                                        />
                                                    </EditPanelSection>
                                                    <EditPanelSection>
                                                        <EditPanelSwitch
                                                            name="useNen2767Advanced"
                                                            placeholder={this.props.t('company.useNen2767Advanced')}
                                                            value={this.state.useNen2767Advanced}
                                                            handleChange={this.handleChange.bind(this)}
                                                            toolTip={this.props.t('company.useNen2767AdvancedExplanation')}
                                                        />
                                                    </EditPanelSection>
                                                </>
                                            )}
                                            {(this.state.isClient || this.state.role === Roles.Administrator) && (
                                                <>
                                                    <EditPanelRow>
                                                        <EditPanelCell rowCount={5}>
                                                            <EditPanelInput
                                                                name="daysVisibilityFutureSurveys"
                                                                placeholder={this.props.t('company.daysVisibilityFutureSurveys')}
                                                                value={this.state.daysVisibilityFutureSurveys}
                                                                icon="fa fa-circle-o"
                                                                handleChange={this.handleChange.bind(this)}
                                                                topLabel={this.props.t('company.daysVisibilityFutureSurveys')}
                                                                topLabelTooltip={this.props.t('company.daysVisibilityFutureSurveysExplanation')}
                                                                restrictToNumbers={true}
                                                            />
                                                        </EditPanelCell>
                                                    </EditPanelRow>
                                                    <EditPanelSection>
                                                        <EditPanelSwitch
                                                            name="useEquipmentManagement"
                                                            placeholder={this.props.t('company.useEquipmentManagement')}
                                                            value={this.state.useEquipmentManagement}
                                                            handleChange={this.handleChange.bind(this)}
                                                        />
                                                    </EditPanelSection>
                                                    <EditPanelSection>
                                                        <EditPanelSwitch
                                                            name="useObjectRelations"
                                                            placeholder={this.props.t('objectRelation.useObjectRelations')}
                                                            value={this.state.useObjectRelations}
                                                            handleChange={this.handleChange.bind(this)}
                                                            toolTip={this.props.t('objectRelation.useObjectRelationsExplanation')}
                                                        />
                                                    </EditPanelSection>
                                                </>
                                            )}
                                            {this.state.isClient && (
                                                <>
                                                    <EditPanelRow>
                                                        <EditPanelCell rowCount={6}>
                                                            <EditPanelInput
                                                                name="authenticationToken"
                                                                topLabel={this.props.t('company.authenticationToken')}
                                                                topLabelTooltip={this.props.t('company.authenticationTokenExplanation')}
                                                                placeholder={this.props.t('company.authenticationToken')}
                                                                value={this.state.authenticationToken}
                                                                icon="fa fa-inbox"
                                                                handleChange={this.handleChange.bind(this)}
                                                            />
                                                        </EditPanelCell>
                                                        <EditPanelCell rowCount={6}>
                                                            <EditPanelInput
                                                                name="restrictedHostName"
                                                                topLabel={this.props.t('company.restrictedHostName')}
                                                                topLabelTooltip={this.props.t('company.restrictedHostNameExplanation')}
                                                                placeholder={this.props.t('company.restrictedHostName')}
                                                                value={this.state.restrictedHostName}
                                                                icon="fa fa-inbox"
                                                                handleChange={this.handleChange.bind(this)}
                                                            />
                                                        </EditPanelCell>
                                                    </EditPanelRow>
                                                </>
                                            )}
                                            {this.state.isClient && (
                                                <>
                                                    <EditPanelHeader text={this.props.t('company.mapSettings')} />
                                                    <EditPanelSection>
                                                        <EditPanelSwitch
                                                            name="shouldClusterMarkers"
                                                            placeholder={this.props.t('company.shouldClusterMarkers')}
                                                            value={this.state.shouldClusterMarkers}
                                                            handleChange={this.handleChange.bind(this)}
                                                        />
                                                    </EditPanelSection>
                                                    <EditPanelSection>
                                                        <EditPanelSwitch
                                                            name="isPdokMapLayerAvailable"
                                                            placeholder={this.props.t('company.isPdokMapLayerAvailable')}
                                                            value={this.state.isPdokMapLayerAvailable}
                                                            handleChange={this.handleChange.bind(this)}
                                                        />
                                                    </EditPanelSection>
                                                    <EditPanelSection>
                                                        <EditPanelSwitch
                                                            name="isDroneNoFlyMapNlAvailable"
                                                            placeholder={this.props.t('company.isDroneNoFlyMapNlAvailable')}
                                                            value={this.state.isDroneNoFlyMapNlAvailable}
                                                            handleChange={this.handleChange.bind(this)}
                                                        />
                                                    </EditPanelSection>
                                                    <EditPanelRow>
                                                        <EditPanelCell rowCount={6}>
                                                            <EditPanelInput
                                                                name="latitude"
                                                                topLabel="Latitude"
                                                                topLabelTooltip={this.props.t('company.mapSettingsExplanation')}
                                                                placeholder={this.props.t('object.latitude')}
                                                                value={this.state.latitude ? this.state.latitude.toString() : ''}
                                                                icon="fa fa-circle-o"
                                                                handleChange={this.handleChange.bind(this)}
                                                                notValid={!this.isValid('latitude')}
                                                            />
                                                            {this.renderHelpText(this.getValidationMessages('latitude'))}
                                                        </EditPanelCell>
                                                        <EditPanelCell rowCount={6}>
                                                            <EditPanelInput
                                                                name="longitude"
                                                                topLabel="Longitude"
                                                                topLabelTooltip={this.props.t('company.mapSettingsExplanation')}
                                                                placeholder={this.props.t('object.longitude')}
                                                                value={this.state.longitude ? this.state.longitude.toString() : ''}
                                                                icon="fa fa-circle-o"
                                                                handleChange={this.handleChange.bind(this)}
                                                                notValid={!this.isValid('longitude')}
                                                            />
                                                            {this.renderHelpText(this.getValidationMessages('longitude'))}
                                                        </EditPanelCell>
                                                    </EditPanelRow>
                                                </>
                                            )}
                                        </>
                                    )}
                                </PanelBody>
                                <EditPanelButtons footer={true}>
                                    <EditPanelButton
                                        onClick={() => this.props.navigate('/companies')}
                                        text={this.props.t('common.cancel')}
                                        small={false}
                                        disabled={this.state.submitting}
                                    />
                                    <EditPanelButton
                                        onClick={() => this.saveClicked(true)}
                                        text={this.props.t('company.saveCompanyAndAddUser')}
                                        small={false}
                                        primary={false}
                                        disabled={this.state.submitting}
                                    />
                                    <EditPanelButton
                                        onClick={() => this.saveClicked(false)}
                                        text={this.props.t('common.save')}
                                        small={false}
                                        primary={true}
                                        disabled={this.state.submitting}
                                    />
                                </EditPanelButtons>
                            </form>
                        </Panel>
                    </Tray>
                </Content>
            </>
        );
    }

    private async load() {
        const url = `/api/companies/${this.props.match.params.id}`;
        const result = await fetchGet(url);
        result.json.storageMb = Math.ceil(result.json.storageLimit / 1024 / 1024);
        await this.setStatePromised(result.json);
    }

    private saveClicked(shouldGoToAddUser: boolean) {
        this.validate(async (validationError: any) => {
            if (validationError) {
                console.log(validationError);
            } else {
                await this.setStatePromised({ submitting: true });
                const data = this.getValidatorData() as any;
                if (data.storageMb) {
                    data.storageLimit = parseInt(data.storageMb, 10) * 1024 * 1024;
                }

                let url = '/api/companies';
                if (this.props.match.params.id) {
                    url += `/${this.props.match.params.id}`;
                }

                const result = await fetchPost(url, {}, data);
                if (shouldGoToAddUser) {
                    this.props.navigate(`/companies/addUser?companyId=${result.json.id}`);
                } else {
                    this.props.navigate(-1);
                }
            }
        });
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(CompanyAddEditComponent as any) as any);
